/*
 * Guest Ministry™ Planner
 * ---------------------------------------------------------------------
 * A person-scoped, occasion-specific worship planner for ministry
 * leaders serving OUTSIDE their home parish.
 *
 * Isolation contract:
 *   • Never merges data into the home Parish Profile or Parish Memory.
 *   • Remembered institution details are labelled as "prior experience",
 *     never as current institutional policy.
 *   • Reuses DossierFolio + HandOff from SundayBuilder for the folio
 *     and export screens — same visual language, same export flow.
 *
 * 5 screens:
 *   1 · Where are you serving?      (institution + role + occasion + date)
 *   2 · What is unique?             (honoree · celebrant · languages · notes)
 *   3 · Discernment                 (background dossier preparation)
 *   4 · Dossier                     (reused DossierFolio)
 *   5 · Hand-off                    (PDF · DOCX · optional Save-to-Memory)
 * =================================================================== */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";
import Discernment from "./sundaybuilder/Discernment";
import DossierFolio from "./sundaybuilder/DossierFolio";
import "./sundaybuilder/workspace.css";
import "./GuestMinistryPlanner.css";
import useSocialMeta from "../hooks/useSocialMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALTAR_URL = `${process.env.PUBLIC_URL || ""}/brand/altar-frontal.webp`;

const OCCASIONS = [
  { key: "funeral",             label: "Funeral" },
  { key: "wedding",             label: "Wedding" },
  { key: "memorial",            label: "Memorial" },
  { key: "sunday_service",      label: "Sunday Service" },
  { key: "ordination",          label: "Ordination" },
  { key: "installation",        label: "Installation" },
  { key: "diocesan_service",    label: "Diocesan Service" },
  { key: "choral_evensong",     label: "Choral Evensong" },
  { key: "lessons_and_carols",  label: "Lessons & Carols" },
  { key: "organ_recital",       label: "Organ Recital" },
  { key: "concert",             label: "Concert" },
  { key: "retreat",             label: "Retreat" },
  { key: "other",               label: "Other" },
];

const ROLES = [
  { key: "organist",             label: "Organist" },
  { key: "music_director",       label: "Music Director" },
  { key: "cantor",               label: "Cantor" },
  { key: "choir_director",       label: "Choir Director" },
  { key: "clergy",               label: "Clergy" },
  { key: "worship_coordinator",  label: "Worship Coordinator" },
  { key: "other",                label: "Other" },
];

const LANGUAGES = ["English", "Spanish", "Latin", "French", "Portuguese",
                   "Vietnamese", "Tagalog", "Korean", "Mandarin", "Other"];


const _OCCASION_DISPLAY = {
  funeral:            "Funeral",
  wedding:            "Wedding",
  memorial:           "Memorial",
  // Neutral, tradition-agnostic. When a service record explicitly
  // indicates Eucharist, downstream surfaces may render "Sunday
  // Eucharist" or "Sunday Mass"; the enum itself never encodes
  // sacramental form.
  sunday_service:     "Sunday Service",
  ordination:         "Ordination",
  installation:       "Installation",
  diocesan_service:   "Diocesan Service",
  choral_evensong:    "Choral Evensong",
  lessons_and_carols: "Lessons & Carols",
  organ_recital:      "Organ Recital",
  concert:            "Concert",
  retreat:            "Retreat",
  other:              "Other",
};

// Plural forms — only irregular cases needed; default is "<label>s".
const _OCCASION_PLURAL = {
  choral_evensong:    "Choral Evensongs",
  lessons_and_carols: "Lessons & Carols services",
  organ_recital:      "Organ Recitals",
  sunday_service:     "Sunday Services",
  diocesan_service:   "Diocesan Services",
};

function _pluralizeOccasion(key, n) {
  const singular = _OCCASION_DISPLAY[key] || key;
  if (n === 1) return `${n} ${singular}`;
  const plural = _OCCASION_PLURAL[key] || `${singular}s`;
  return `${n} ${plural}`;
}

function _formatServedDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day:   "numeric",
      month: "long",
      year:  "numeric",
    });
  } catch { return ""; }
}

/*
 * Recurrence strip — factual only. Renders the four consented signals
 * described in the Rule A directive:
 *   • "You have served here N times." — ONLY when service_count ≥ 2
 *   • "Last served: <date>"
 *   • "Most recent occasion: <label>"
 *   • "<N> notes previously preserved" — ONLY when N > 0
 *
 * No badges. No streaks. No loyalty language. No inferred preferences.
 * Any row whose data is absent is omitted entirely (Rule 0).
 */
function RecurrenceStrip({ memoryHit }) {
  const rows = [];
  const count = Number(memoryHit.service_count || 0);
  if (count >= 2) {
    rows.push({
      key: "count",
      testid: "gm-recurrence-visit-count",
      body: `You have served here ${count} times.`,
    });
  }

  // Occasion breakdown — sorted by count desc, then alphabetical by
  // display label. Every entry uses factual counts from
  // memoryHit.occasion_counts. Never inferred from anything else.
  const counts = memoryHit.occasion_counts && typeof memoryHit.occasion_counts === "object"
    ? Object.entries(memoryHit.occasion_counts).filter(([, v]) => Number(v) > 0)
    : [];
  if (counts.length > 0) {
    const sorted = counts.slice().sort((a, b) => {
      if (b[1] !== a[1]) return Number(b[1]) - Number(a[1]);
      const la = _OCCASION_DISPLAY[a[0]] || a[0];
      const lb = _OCCASION_DISPLAY[b[0]] || b[0];
      return la.localeCompare(lb);
    });
    rows.push({
      key: "breakdown",
      testid: "gm-recurrence-breakdown",
      body: sorted.map(([k, v]) => _pluralizeOccasion(k, Number(v))).join(" · "),
    });
  }

  const distinct = Number(memoryHit.distinct_ministry_types || 0);
  if (distinct >= 2) {
    rows.push({
      key: "distinct",
      testid: "gm-recurrence-distinct-types",
      body: `Different ministry types served: ${distinct}`,
    });
  }

  const lastServed = _formatServedDate(memoryHit.last_served_at);
  if (lastServed) {
    rows.push({
      key: "last",
      testid: "gm-recurrence-last-served",
      body: `Last served: ${lastServed}`,
    });
  }
  const occ = memoryHit.most_recent_occasion;
  if (occ) {
    const label = _OCCASION_DISPLAY[occ] || occ;
    rows.push({
      key: "occasion",
      testid: "gm-recurrence-most-recent",
      body: `Most recent occasion: ${label}`,
    });
  }
  const noteCount = Number(memoryHit.note_count || 0);
  if (noteCount > 0) {
    const noun = noteCount === 1 ? "note" : "notes";
    rows.push({
      key: "notes",
      testid: "gm-recurrence-note-count",
      body: `${noteCount} ${noun} previously preserved`,
    });
  }
  if (rows.length === 0) return null;
  return (
    <ul className="gm-recurrence" data-testid="gm-recurrence-strip">
      {rows.map((r) => (
        <li key={r.key} data-testid={r.testid}>{r.body}</li>
      ))}
    </ul>
  );
}

/* ============================================================== */
/* Screen 1 · Where are you serving?                              */
/* ============================================================== */
function WhereAreYouServing({ initial, onNext }) {
  const [institution, setInstitution] = useState(initial.institution || "");
  const [denomination, setDenomination] = useState(initial.denomination || "");
  const [city, setCity] = useState(initial.city || "");
  const [state, setState] = useState(initial.state_region || "");
  const [role, setRole] = useState(initial.role || "organist");
  const [occasion, setOccasion] = useState(initial.occasion || "funeral");
  const [occasionOther, setOccasionOther] = useState(initial.occasion_other || "");
  const [serviceDate, setServiceDate] = useState(initial.service_date || "");
  const [memoryHit, setMemoryHit] = useState(null);

  // Search remembered institutions while the minister types. Debounced.
  useEffect(() => {
    if (!institution || institution.trim().length < 2) { setMemoryHit(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/guest-ministry/institutions/search`, {
          params: { q: institution.trim() },
        });
        const first = (r.data?.results || [])[0];
        setMemoryHit(first || null);
      } catch { setMemoryHit(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [institution]);

  const valid = institution.trim().length > 0 && serviceDate;

  return (
    <div className="sb-screen" data-testid="gm-screen-1">
      <h1 className="sb-h1">Where are you serving?</h1>
      <p className="gm-support">
        Prepare a dossier for a liturgy at an institution outside your home
        parish. Your Parish Profile and Parish Memory will not be affected.
      </p>

      {memoryHit && (
        <div className="gm-memory-hit" data-testid="gm-memory-hit">
          <p className="gm-memory-hit__label">We remember this institution</p>
          <p className="gm-memory-hit__name" data-testid="gm-memory-name">{memoryHit.institution_name}</p>
          <RecurrenceStrip memoryHit={memoryHit} />
          <p className="gm-memory-hit__policy" data-testid="gm-memory-guardrail">
            This is your personal ministry experience at this institution —
            not the institution&rsquo;s official policy or preference.
          </p>
        </div>
      )}

      <div className="gm-grid">
        <div className="gm-field gm-field--wide">
          <label htmlFor="gm-institution">Institution name</label>
          <input id="gm-institution" type="text" value={institution}
                 onChange={(e) => setInstitution(e.target.value)}
                 placeholder="Trinity Lutheran Church"
                 data-testid="gm-institution-input" autoComplete="off" />
        </div>
        <div className="gm-field">
          <label htmlFor="gm-denom">Denomination (optional)</label>
          <input id="gm-denom" type="text" value={denomination}
                 onChange={(e) => setDenomination(e.target.value)}
                 placeholder="Roman Catholic, Lutheran (ELCA), Episcopal…"
                 data-testid="gm-denomination-input" />
        </div>
        <div className="gm-field">
          <label htmlFor="gm-city">City</label>
          <input id="gm-city" type="text" value={city}
                 onChange={(e) => setCity(e.target.value)}
                 data-testid="gm-city-input" />
        </div>
        <div className="gm-field">
          <label htmlFor="gm-state">State / Region</label>
          <input id="gm-state" type="text" value={state}
                 onChange={(e) => setState(e.target.value)}
                 data-testid="gm-state-input" />
        </div>
        <div className="gm-field">
          <label htmlFor="gm-role">Your role</label>
          <select id="gm-role" value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="gm-role-select">
            {ROLES.map((r) => (<option key={r.key} value={r.key}>{r.label}</option>))}
          </select>
        </div>
        <div className="gm-field">
          <label htmlFor="gm-occasion">Occasion</label>
          <select id="gm-occasion" value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  data-testid="gm-occasion-select">
            {OCCASIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
          </select>
        </div>
        {occasion === "other" && (
          <div className="gm-field gm-field--wide">
            <label htmlFor="gm-occ-other">Describe the occasion</label>
            <input id="gm-occ-other" type="text" value={occasionOther}
                   onChange={(e) => setOccasionOther(e.target.value)}
                   data-testid="gm-occasion-other-input" />
          </div>
        )}
        <div className="gm-field">
          <label htmlFor="gm-date">Service date</label>
          <input id="gm-date" type="datetime-local" value={serviceDate}
                 onChange={(e) => setServiceDate(e.target.value)}
                 data-testid="gm-date-input" />
        </div>
      </div>

      <div className="gm-actions">
        <button className="sb-btn primary" disabled={!valid}
                onClick={() => onNext({
                  institution: institution.trim(),
                  denomination: denomination.trim() || undefined,
                  city: city.trim() || undefined,
                  state_region: state.trim() || undefined,
                  role, occasion,
                  occasion_other: occasion === "other" ? occasionOther.trim() : undefined,
                  service_date: serviceDate,
                })}
                data-testid="gm-screen-1-continue">
          Continue <ChevronRight size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}


/* ============================================================== */
/* Screen 2 · What is unique?                                    */
/* ============================================================== */
function WhatIsUnique({ initial, onBack, onPrepare }) {
  const [honoree, setHonoree] = useState(initial.deceased_or_honoree_name || "");
  const [celebrant, setCelebrant] = useState(initial.celebrant_name || "");
  const [lang, setLang] = useState(initial.service_language || []);
  const [notes, setNotes] = useState(initial.notes || "");
  const [preserve, setPreserve] = useState(false);

  const toggleLang = (l) => {
    setLang((prev) => prev.includes(l)
      ? prev.filter((x) => x !== l)
      : [...prev, l]);
  };

  const isFuneralOrMemorial = ["funeral", "memorial"].includes(initial.occasion);

  return (
    <div className="sb-screen" data-testid="gm-screen-2">
      <h1 className="sb-h1">What is unique about this service?</h1>
      <p className="gm-support">
        Tell us what the platform cannot already know from the host
        institution and the date.
      </p>

      <div className="gm-grid">
        {isFuneralOrMemorial && (
          <div className="gm-field gm-field--wide">
            <label htmlFor="gm-honoree">Name of the deceased or honoree</label>
            <input id="gm-honoree" type="text" value={honoree}
                   onChange={(e) => setHonoree(e.target.value)}
                   data-testid="gm-honoree-input" />
          </div>
        )}
        <div className="gm-field gm-field--wide">
          <label htmlFor="gm-celebrant">Celebrant (optional)</label>
          <input id="gm-celebrant" type="text" value={celebrant}
                 onChange={(e) => setCelebrant(e.target.value)}
                 data-testid="gm-celebrant-input" />
        </div>
        <div className="gm-field gm-field--wide">
          <label>Languages</label>
          <div className="gm-chips" data-testid="gm-language-chips">
            {LANGUAGES.map((l) => (
              <button type="button"
                      key={l}
                      className={`gm-chip ${lang.includes(l) ? "on" : ""}`}
                      onClick={() => toggleLang(l)}
                      data-testid={`gm-lang-${l.toLowerCase()}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="gm-field gm-field--wide">
          <label htmlFor="gm-notes">Optional notes</label>
          <textarea id="gm-notes" value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything unique about this service — a soloist, a family request, a pastoral emphasis…"
                    data-testid="gm-notes-textarea" />
        </div>
      </div>

      <label className="gm-consent" data-testid="gm-preserve-checkbox">
        <input type="checkbox" checked={preserve}
               onChange={(e) => setPreserve(e.target.checked)}
               data-testid="gm-preserve-input" />
        Save this institution to Ministry Memory™
        <span className="gm-consent__hint">
          Person-scoped, never merged with your parish. You can edit or
          delete it anytime.
        </span>
      </label>

      <div className="gm-actions gm-actions--split">
        <button className="sb-btn ghost" onClick={onBack} data-testid="gm-screen-2-back">
          <ChevronLeft size={18} strokeWidth={2.5} /> Back
        </button>
        <button className="sb-btn primary"
                onClick={() => onPrepare({
                  deceased_or_honoree_name: honoree.trim() || undefined,
                  celebrant_name: celebrant.trim() || undefined,
                  service_language: lang,
                  notes: notes.trim() || undefined,
                }, preserve)}
                data-testid="gm-prepare-dossier">
          Prepare dossier <ChevronRight size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}


/* ============================================================== */
/* Screen 4 · Dossier (reuses DossierFolio + adds banner)         */
/* ============================================================== */
function GuestDossier({ report, onContinueExport, onRefine }) {
  return (
    <>
      {report.banner && (
        <div className="gm-banner" data-testid="gm-banner" role="alert">
          <span className="gm-banner__icon" aria-hidden="true">!</span>
          <p>{report.banner}</p>
        </div>
      )}

      {report.host_confirmation_required && (
        <section className="gm-hcr" data-testid="gm-host-confirmation-required">
          <h2>{report.host_confirmation_required.title}</h2>
          <ul>
            {(report.host_confirmation_required.items || []).map((item) => (
              <li key={item} data-testid={`gm-hcr-item-${item.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      {report.prior_experience && (
        <section className="gm-prior" data-testid="gm-prior-experience">
          <p className="gm-prior__label">{report.prior_experience.label}</p>
          {report.prior_experience.detail?.available_hymnals?.length > 0 && (
            <p><em>Remembered hymnals:</em> {report.prior_experience.detail.available_hymnals.join(", ")}</p>
          )}
          {report.prior_experience.detail?.clergy_preferences?.length > 0 && (
            <p><em>Remembered clergy notes:</em> {report.prior_experience.detail.clergy_preferences.join(" · ")}</p>
          )}
          <p className="gm-prior__note">
            These details are not host-confirmed policy — confirm, edit,
            or disregard them before the service.
          </p>
        </section>
      )}

      <DossierFolio
        plan={report}
        jobId={null}
        explain={null}
        memoryPreview={null}
        subscribed={true}
        sundayLabel={report.title}
        onRefine={onRefine}
        onContinueExport={onContinueExport}
        onPlanUpdate={() => {}}
      />
    </>
  );
}


/* ============================================================== */
/* Screen 5 · Hand-off                                            */
/* ============================================================== */
function GuestHandoff({ report, planId, alreadyPreserved, onPreserve, onNewPlan }) {
  const [saved, setSaved] = useState(alreadyPreserved);
  const [busy, setBusy] = useState(false);
  const download = () => window.print();

  const preserve = async () => {
    setBusy(true);
    try {
      await onPreserve();
      setSaved(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="sb-screen gm-handoff" data-testid="gm-handoff">
      <h1 className="gm-handoff__title">Take it into the parish.</h1>
      <p className="gm-handoff__sub">Your Guest Ministry™ dossier is prepared.</p>
      <div className="gm-handoff__actions">
        <button className="sb-btn primary" onClick={download} data-testid="gm-download-pdf">
          <Download size={16} strokeWidth={2.5} /> Download PDF
        </button>
        <button className="sb-btn primary" onClick={download} data-testid="gm-download-docx">
          <FileText size={16} strokeWidth={2.5} /> Download DOCX
        </button>
        {!saved ? (
          <button className="sb-btn ghost" onClick={preserve} disabled={busy} data-testid="gm-save-memory">
            {busy ? "Saving…" : "Save to Ministry Memory"}
          </button>
        ) : (
          <span className="gm-handoff__saved" data-testid="gm-saved-badge">
            Saved to Ministry Memory
          </span>
        )}
      </div>
      <button onClick={onNewPlan} data-testid="gm-prepare-another" className="gm-handoff__another">
        Prepare another Guest Ministry dossier →
      </button>
      <div className="report-print-area" style={{ display: "none" }}>
        <h1>{report.title}</h1>
        {report.overview && <p>{report.overview}</p>}
        {(report.services || []).map((s, i) => (
          <div key={i}>
            <h2>{s.name} · {s.occasion}</h2>
            {(s.hymns || []).map((h, j) => (
              <div key={j}>{h.title} — {h.placement}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ============================================================== */
/* Main component                                                 */
/* ============================================================== */
export default function GuestMinistryPlanner() {
  useSocialMeta({
    title: "Guest Ministry™ — Serve every family with care",
    description: "Prepare off-site liturgies with the same confidence as your own parish. The Guest Ministry™ workspace inside SundayBuilder™.",
    path: "/guest-ministry",
  });
  const navigate = useNavigate();
  const [screen, setScreen] = useState("where");
  const [screen1Data, setScreen1Data] = useState({});
  const [planId, setPlanId] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [alreadyPreserved, setAlreadyPreserved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.documentElement.style.setProperty("--li-altar-frontal-url", `url("${ALTAR_URL}")`);
  }, []);

  const goScreen2 = (data) => { setScreen1Data(data); setScreen("unique"); };

  const preparePipeline = async (screen2Data, preserveConsent) => {
    setError(null);
    // Build the plan payload.
    const isoDate = new Date(screen1Data.service_date).toISOString();
    const body = {
      role:     screen1Data.role,
      occasion: screen1Data.occasion,
      occasion_other: screen1Data.occasion_other,
      service_date: isoDate,
      institution: {
        institution_name: screen1Data.institution,
        denomination: screen1Data.denomination,
        city: screen1Data.city,
        state_region: screen1Data.state_region,
      },
      ...screen2Data,
      preserve_to_ministry_memory: preserveConsent,
    };
    // Strip undefined so extra="forbid" is happy.
    Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
    Object.keys(body.institution).forEach((k) => body.institution[k] === undefined && delete body.institution[k]);

    try {
      const create = await axios.post(`${API}/guest-ministry/plans`, body);
      const pid = create.data.id;
      setPlanId(pid);
      setScreen("discernment");
      const prep = await axios.post(`${API}/guest-ministry/plans/${pid}/prepare`);
      // Poll
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/guest-ministry/plans/${pid}/dossier`);
          if (r.data.status === "done") {
            setDossier(r.data.report || {});
            setScreen("dossier");
            return;
          }
          if (attempts > 40) {
            setError("Preparation is taking longer than expected. Please try again.");
            setScreen("unique");
            return;
          }
        } catch (_) {
          if (attempts > 40) {
            setError("We lost contact with the archive. Please try again.");
            setScreen("unique");
            return;
          }
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2000);
      // If consent was given, also persist to memory immediately (server keeps consent audit).
      if (preserveConsent) {
        try {
          await axios.post(`${API}/guest-ministry/plans/${pid}/preserve-memory`, {});
          setAlreadyPreserved(true);
        } catch (_) { /* consent is best-effort; user can save from screen 5 */ }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not begin preparation. Please try again.");
      setScreen("unique");
    }
  };

  const preserveFromHandoff = useCallback(async () => {
    if (!planId) return;
    await axios.post(`${API}/guest-ministry/plans/${planId}/preserve-memory`, {});
  }, [planId]);

  const startOver = () => {
    setScreen("where"); setScreen1Data({}); setPlanId(null); setDossier(null);
    setAlreadyPreserved(false); setError(null);
  };

  return (
    <div className="sb-workspace" data-testid="guest-ministry-page">
      <div className="sb-topbar" data-testid="gm-topbar">
        <div className="sb-brand">Guest<em>Ministry</em>™</div>
        <button className="sb-back" onClick={() => navigate("/dashboard")} data-testid="gm-back">
          ← Dashboard
        </button>
      </div>

      {screen === "where" && (
        <div className="sb-altar-hero" role="img" aria-label="Bas-relief altar frontal">
          <div className="sb-altar-hero__body">
            <div className="sb-altar-hero__brand">
              <img className="sb-altar-hero__monogram"
                   src={`${process.env.PUBLIC_URL || ""}/brand/li-monogram.png`}
                   alt="" aria-hidden="true"
                   onError={(e) => { e.target.style.display = "none"; }} />
              <span className="sb-altar-hero__title">Guest Ministry™</span>
            </div>
            <span className="sb-altar-hero__wordmark">Person-scoped Planning Desk</span>
          </div>
        </div>
      )}

      {error && screen !== "discernment" && (
        <div className="sb-screen">
          <div className="gm-banner" role="alert" data-testid="gm-error-banner">
            <span className="gm-banner__icon" aria-hidden="true">!</span>
            <p>{error}</p>
          </div>
        </div>
      )}

      {screen === "where" && (
        <WhereAreYouServing
          initial={screen1Data}
          onNext={goScreen2}
        />
      )}
      {screen === "unique" && (
        <WhatIsUnique
          initial={{ ...screen1Data }}
          onBack={() => setScreen("where")}
          onPrepare={preparePipeline}
        />
      )}
      {screen === "discernment" && <Discernment />}
      {screen === "dossier" && dossier && (
        <GuestDossier report={dossier}
                      onContinueExport={() => setScreen("handoff")}
                      onRefine={() => setScreen("unique")} />
      )}
      {screen === "handoff" && dossier && (
        <GuestHandoff report={dossier} planId={planId}
                      alreadyPreserved={alreadyPreserved}
                      onPreserve={preserveFromHandoff}
                      onNewPlan={startOver} />
      )}
    </div>
  );
}
